import React, { useState } from 'react';
import { Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

interface AmortizationState {
  assetValue: string;
  usefulLife: string;
  salvageValue: string;
  method: 'linear' | 'declining';
  currency: 'EUR' | 'BAM' | 'RSD';
}

interface AmortizationResult {
  yearlyDepreciation: number;
  schedule: {
    year: number;
    depreciation: number;
    bookValue: number;
  }[];
}

const currencySymbols: Record<string, string> = {
  EUR: '€',
  BAM: 'KM',
  RSD: 'RSD'
};

function KalkulatorAmortizacije() {
  const [values, setValues] = useState<AmortizationState>({
    assetValue: '',
    usefulLife: '',
    salvageValue: '',
    method: 'linear',
    currency: 'EUR'
  });

  const [results, setResults] = useState<AmortizationResult | null>(null);

  const calculateAmortization = () => {
    const initialValue = parseFloat(values.assetValue);
    const years = parseInt(values.usefulLife);
    const salvageValue = parseFloat(values.salvageValue) || 0;

    if (isNaN(initialValue) || isNaN(years) || years <= 0) {
      return null;
    }

    const schedule = [];
    let currentValue = initialValue;
    let yearlyDepreciation;

    if (values.method === 'linear') {
      yearlyDepreciation = (initialValue - salvageValue) / years;
      
      for (let year = 1; year <= years; year++) {
        currentValue -= yearlyDepreciation;
        schedule.push({
          year,
          depreciation: yearlyDepreciation,
          bookValue: Math.max(currentValue, salvageValue)
        });
      }
    } else {
      // Declining balance method
      const rate = 2 / years; // Double declining rate
      
      for (let year = 1; year <= years; year++) {
        yearlyDepreciation = currentValue * rate;
        
        // Switch to straight-line if it gives higher depreciation
        const remainingYears = years - year + 1;
        const straightLineDepreciation = (currentValue - salvageValue) / remainingYears;
        
        if (straightLineDepreciation > yearlyDepreciation) {
          yearlyDepreciation = straightLineDepreciation;
        }
        
        currentValue -= yearlyDepreciation;
        
        if (currentValue < salvageValue) {
          yearlyDepreciation -= (salvageValue - currentValue);
          currentValue = salvageValue;
        }
        
        schedule.push({
          year,
          depreciation: yearlyDepreciation,
          bookValue: currentValue
        });
      }
    }

    return {
      yearlyDepreciation: schedule[0].depreciation,
      schedule
    };
  };

  const handleInputChange = (field: keyof AmortizationState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCalculate = () => {
    const results = calculateAmortization();
    setResults(results);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Amortizacije",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte amortizaciju imovine linearnom ili degresivnom metodom. Precizni izračuni s detaljnim prikazom po godinama.",
    "url": "https://online-kalkulator.com/kalkulator-amortizacije",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Kalkulator Amortizacije | Izračun Amortizacije Imovine"
        description="Izračunajte amortizaciju imovine linearnom ili degresivnom metodom. Precizni izračuni s detaljnim prikazom po godinama."
        canonicalUrl="/kalkulator-amortizacije"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Amortizacije</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vrijednost imovine
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.assetValue}
                      onChange={(e) => handleInputChange('assetValue', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Unesite iznos u ${values.currency}`}
                    />
                    <span className="absolute right-3 top-2 text-gray-500">
                      {currencySymbols[values.currency]}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Korisni vijek trajanja (godine)
                  </label>
                  <input
                    type="number"
                    value={values.usefulLife}
                    onChange={(e) => handleInputChange('usefulLife', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ostatak vrijednosti
                  </label>
                  <input
                    type="number"
                    value={values.salvageValue}
                    onChange={(e) => handleInputChange('salvageValue', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Neobavezno"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Metoda amortizacije
                  </label>
                  <select
                    value={values.method}
                    onChange={(e) => handleInputChange('method', e.target.value as 'linear' | 'declining')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="linear">Linearna</option>
                    <option value="declining">Degresivna</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valuta
                  </label>
                  <select
                    value={values.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value as AmortizationState['currency'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="EUR">Euro (€)</option>
                    <option value="BAM">Konvertibilna marka (KM)</option>
                    <option value="RSD">Srpski dinar (RSD)</option>
                  </select>
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Izračunaj
              </button>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center py-3 px-4 rounded-lg bg-gray-50">
                    <p className="text-gray-600 text-sm mb-1">Godišnja amortizacija</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(results.yearlyDepreciation)} {currencySymbols[values.currency]}
                    </p>
                  </div>

                  {/* Amortization Schedule */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Godina
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amortizacija
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Knjigovodstvena vrijednost
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {results.schedule.map((row) => (
                          <tr key={row.year}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {row.year}.
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatCurrency(row.depreciation)} {currencySymbols[values.currency]}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatCurrency(row.bookValue)} {currencySymbols[values.currency]}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Amortizacije</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator amortizacije je koristan alat koji vam pomaže izračunati godišnju
                  amortizaciju imovine i pratiti njenu knjigovodstvenu vrijednost tijekom vremena.
                  Podržava linearnu i degresivnu metodu amortizacije.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Metode Amortizacije</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Linearna metoda</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Jednaki iznosi amortizacije svake godine</li>
                      <li>Jednostavna za izračun i razumijevanje</li>
                      <li>Pogodna za imovinu koja se ravnomjerno troši</li>
                      <li>Formula: (Nabavna vrijednost - Ostatak vrijednosti) / Broj godina</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Degresivna metoda</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Veći iznosi amortizacije u početnim godinama</li>
                      <li>Odražava brže trošenje imovine u početku</li>
                      <li>Pogodna za tehnološku opremu</li>
                      <li>Koristi dvostruku stopu linearne metode</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite nabavnu vrijednost imovine</li>
                  <li>Odredite korisni vijek trajanja u godinama</li>
                  <li>Unesite ostatak vrijednosti (ako postoji)</li>
                  <li>Odaberite metodu amortizacije</li>
                  <li>Odaberite valutu za prikaz</li>
                  <li>Kliknite "Izračunaj" za rezultate</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Svi iznosi su zaokruženi na dvije decimale</li>
                  <li>Ostatak vrijednosti je neobavezan</li>
                  <li>Korisni vijek trajanja mora biti pozitivan broj</li>
                  <li>Degresivna metoda automatski prelazi na linearnu kada daje veću amortizaciju</li>
                  <li>Izračuni su informativne prirode</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Primjena Amortizacije</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Računovodstveno praćenje vrijednosti imovine</li>
                  <li>Porezno planiranje</li>
                  <li>Financijsko izvještavanje</li>
                  <li>Planiranje zamjene imovine</li>
                  <li>Procjena troškova poslovanja</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorAmortizacije;